import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Checkbox, Divider, FormControlLabel } from '@mui/material';
import { RootState } from 'src/store/store';
import { getLabelFallback, range } from '@utils/utils';
import { IAddToCartNewPrescriptionPayload, IContactLensForm } from '@interfaces/contactsInterfaces';
import CLDiscounts from '../my-new-prescription/CLDiscounts';
import FormColumn from './FormColumn';
import FormDoubleColumn from './FormDoubleColumn';
import { cleanContactLensForm, getInitialValue } from '@utils/pages-utils/ContactsUtils';
import useOnFormSubmit from '@hooks/useOnFormSubmit';
import useOnFormChange from '@hooks/useOnFormChange';
import { useEffect } from 'react';

const NewPrescriptionForm = (): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);
    const currentProduct = useSelector((state: RootState) => state.contacts.currentProduct);

    const {
        power: powerValueOptions,
        baseCurve: bcValueOptions,
        diameter: diaValueOptions,
        cylinder: cylValueOptions,
        axis: axisValueOptions,
        add: addValueOptions,
        color: colorValueOptions,
        dominance: dominanceValueOptions,
    } = currentProduct?.attributes;

    const boxesValueOptions = range(1, config.maxBoxes, true).map(String);
    const selectedBoxesValue = config.defaultBoxes.toString();

    const initialState: IContactLensForm = {
        rxLeftEye: true,
        leftQuantity: selectedBoxesValue,
        rxLeftAdd: getInitialValue(addValueOptions),
        rxLeftAxis: getInitialValue(axisValueOptions),
        rxLeftBc: getInitialValue(bcValueOptions),
        rxLeftColor: getInitialValue(colorValueOptions),
        rxLeftCyl: getInitialValue(cylValueOptions),
        rxLeftDia: getInitialValue(diaValueOptions),
        rxLeftPower: getInitialValue(powerValueOptions),
        rxLeftDominance: getInitialValue(dominanceValueOptions),
        rxRightEye: true,
        rightQuantity: selectedBoxesValue,
        rxRightAdd: getInitialValue(addValueOptions),
        rxRightAxis: getInitialValue(axisValueOptions),
        rxRightBc: getInitialValue(bcValueOptions),
        rxRightColor: getInitialValue(colorValueOptions),
        rxRightCyl: getInitialValue(cylValueOptions),
        rxRightDia: getInitialValue(diaValueOptions),
        rxRightPower: getInitialValue(powerValueOptions),
        rxRightDominance: getInitialValue(dominanceValueOptions),
        rxSameEyes: false,
    };

    const schema = yup.object().shape({
        rxSameEyes: yup.boolean(),
        rxLeftEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxLeftEye, ctx) => rxLeftEye || ctx.parent.rxRightEye || ctx.parent.rxSameEyes
            ),
        rxRightEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxRightEye, ctx) => rxRightEye || ctx.parent.rxLeftEye
            ),
        leftQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxLeftEye ? parseInt(val) > 0 : true
            ),
        rightQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxRightEye ? parseInt(val) > 0 : true
            ),
        rxLeftAdd: yup
            .string()
            .test('invalid', 'rxLeftAdd', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && addValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftAxis: yup
            .string()
            .test('invalid', 'rxLeftAxis', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && axisValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftBc: yup
            .string()
            .test('invalid', 'rxLeftBc', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && bcValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftColor: yup
            .string()
            .test('invalid', 'rxLeftColor', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && colorValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftCyl: yup
            .string()
            .test('invalid', 'rxLeftCyl', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && cylValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftDia: yup
            .string()
            .test('invalid', 'rxLeftDia', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && diaValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftPower: yup
            .string()
            .test('invalid', 'rxLeftPower', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && powerValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftDominance: yup
            .string()
            .test('invalid', 'rxLeftDominance', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.rxSameEyes && dominanceValueOptions?.length
                    ? !!val
                    : true
            ),
        rxRightAdd: yup
            .string()
            .test('invalid', 'rxRightAdd', (val, ctx) =>
                ctx.parent.rxRightEye && addValueOptions?.length ? !!val : true
            ),
        rxRightAxis: yup
            .string()
            .test('invalid', 'rxRightAxis', (val, ctx) =>
                ctx.parent.rxRightEye && axisValueOptions?.length ? !!val : true
            ),
        rxRightBc: yup
            .string()
            .test('invalid', 'rxRightBc', (val, ctx) =>
                ctx.parent.rxRightEye && bcValueOptions?.length ? !!val : true
            ),
        rxRightColor: yup
            .string()
            .test('invalid', 'rxRightColor', (val, ctx) =>
                ctx.parent.rxRightEye && colorValueOptions?.length ? !!val : true
            ),
        rxRightCyl: yup
            .string()
            .test('invalid', 'rxRightCyl', (val, ctx) =>
                ctx.parent.rxRightEye && cylValueOptions?.length ? !!val : true
            ),
        rxRightDia: yup
            .string()
            .test('invalid', 'rxRightDia', (val, ctx) =>
                ctx.parent.rxRightEye && diaValueOptions?.length ? !!val : true
            ),
        rxRightPower: yup
            .string()
            .test('invalid', 'rxRightPower', (val, ctx) =>
                ctx.parent.rxRightEye && powerValueOptions?.length ? !!val : true
            ),
        rxRightDominance: yup
            .string()
            .test('invalid', 'rxRightDominance', (val, ctx) =>
                ctx.parent.rxRightEye && dominanceValueOptions?.length ? !!val : true
            ),
    });

    const methods = useForm<IContactLensForm>({
        resolver: yupResolver(schema),
        defaultValues: initialState,
    });

    const watchLeftEnabled = methods.watch('rxLeftEye');
    const watchRightEnabled = methods.watch('rxRightEye');
    const watchBothEnabled = methods.watch('rxSameEyes');
    const watchForm = useWatch({ control: methods.control }) as IContactLensForm;

    useEffect(() => {
        if (watchBothEnabled) {
            methods.setValue('rxLeftAdd', watchForm.rxRightAdd);
            methods.setValue('rxLeftAxis', watchForm.rxRightAxis);
            methods.setValue('rxLeftBc', watchForm.rxRightBc);
            methods.setValue('rxLeftColor', watchForm.rxRightColor);
            methods.setValue('rxLeftCyl', watchForm.rxRightCyl);
            methods.setValue('rxLeftDia', watchForm.rxRightDia);
            methods.setValue('rxLeftPower', watchForm.rxRightPower);
            methods.setValue('rxLeftDominance', watchForm.rxRightDominance);
            methods.setValue('leftQuantity', watchForm.rightQuantity);
        }
    }, [
        watchBothEnabled,
        watchForm.rxRightAdd,
        watchForm.rxRightAxis,
        watchForm.rxRightBc,
        watchForm.rxRightColor,
        watchForm.rxRightCyl,
        watchForm.rxRightDia,
        watchForm.rxRightPower,
        watchForm.rxRightDominance,
        watchForm.rightQuantity,
    ]);

    useOnFormChange({
        ...cleanContactLensForm(watchForm),
        leftIsCurrentProduct: true,
        rightIsCurrentProduct: true,
    });

    const onSubmit: SubmitHandler<IContactLensForm> = (data: IContactLensForm) => {
        const submitData: IAddToCartNewPrescriptionPayload = {
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            catalogId: config.catalogId,
            langId: config.langId,
            action: 'add',
            cache: false,
            errorViewName: 'LogonAjaxView',
            orderId: '.',
            partType: 'CONTACT_LENSES',
            URL: '',
            productId: config.productId,
            ...cleanContactLensForm(data),
        };

        // reverse mapping CL_DOMINANCE
        if (!!submitData.rxRightDominance) {
            submitData.rxRightDominance = submitData.rxRightDominance === 'D' ? 'True' : 'False';
        }
        if (!!submitData.rxLeftDominance) {
            submitData.rxLeftDominance = submitData.rxLeftDominance === 'D' ? 'True' : 'False';
        }

        document.getElementById('prescription-error-cl').classList.add('hide');
        config.onFormSubmit(submitData, true);
        console.log('>>> New CL prescription form submit', submitData);
    };

    const onInvalid = error => {
        config.onFormSubmit(null, false);
        console.error('>>> New CL prescription form invalid', error);
    };

    useOnFormSubmit(methods, onSubmit, onInvalid);

    return (
        <FormProvider {...methods}>
            <form
                id={config.prescriptionFormId}
                className="contact-lens-prescription-content"
                onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
            >
                <div className="eye-container-checkbox">
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...methods.register('rxSameEyes')}
                                size="small"
                                inputProps={{
                                    'aria-label': 'Check OS (right)',
                                }}
                            />
                        }
                        label={getLabelFallback('SAME_PRESCRIPTION_BOTH_EYES')}
                    />
                </div>

                <div className="form-container">
                    {/* EYE CHECK */}
                    <div className="eye-column param-column">
                        <div className="param-label eye-label">
                            <label>
                                {getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_LABEL'
                                )}
                            </label>
                        </div>

                        <div className="presc-param-select eye-select right">
                            <div className="eye-container-checkbox">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...methods.register('rxRightEye')}
                                            size="small"
                                            inputProps={{
                                                'aria-label': 'Check OD (right)',
                                            }}
                                            defaultChecked
                                        />
                                    }
                                    label={getLabelFallback(
                                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_RIGHT_LABEL'
                                    )}
                                />
                            </div>
                        </div>
                        <div className="presc-param-select eye-select left">
                            <div className="eye-container-checkbox">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...methods.register('rxLeftEye')}
                                            size="small"
                                            inputProps={{
                                                'aria-label': 'Check OS (left)',
                                            }}
                                            defaultChecked
                                            disabled={watchBothEnabled}
                                        />
                                    }
                                    label={getLabelFallback(
                                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_LEFT_LABEL'
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* POWER PARAM */}
                    <FormDoubleColumn
                        attribute="power"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_POWER_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftPower',
                            values: powerValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightPower',
                            values: powerValueOptions,
                        }}
                    />

                    {/* BC PARAM */}
                    <FormColumn
                        attribute="bc"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BC_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftBc',
                            values: bcValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightBc',
                            values: bcValueOptions,
                        }}
                    />

                    {/* DIA PARAM */}
                    <FormColumn
                        attribute="dia"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DIA_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftDia',
                            values: diaValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightDia',
                            values: diaValueOptions,
                        }}
                    />

                    {/* CYL PARAM */}
                    <FormColumn
                        attribute="cyl"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_CYL_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftCyl',
                            values: cylValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightCyl',
                            values: cylValueOptions,
                        }}
                    />

                    {/* AXIS PARAM */}
                    <FormColumn
                        attribute="axis"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_AXIS_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftAxis',
                            values: axisValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightAxis',
                            values: axisValueOptions,
                        }}
                    />

                    {/* ADD PARAM */}
                    <FormColumn
                        attribute="add"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_ADD_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftAdd',
                            values: addValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightAdd',
                            values: addValueOptions,
                        }}
                    />

                    {/* COLOR PARAM */}
                    <FormColumn
                        attribute="color"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_COLOR_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled && !watchBothEnabled,
                            name: 'rxLeftColor',
                            values: colorValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            name: 'rxRightColor',
                            values: colorValueOptions,
                        }}
                    />

                    {/* DOMINANCE PARAM */}
                    {dominanceValueOptions.length > 1 && (
                        <FormColumn
                            attribute="dominance"
                            label={getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DOMINANCE_LABEL'
                            )}
                            left={{
                                enabled: watchLeftEnabled && !watchBothEnabled,
                                name: 'rxLeftDominance',
                                values: dominanceValueOptions,
                            }}
                            right={{
                                enabled: watchRightEnabled,
                                name: 'rxRightDominance',
                                values: dominanceValueOptions,
                            }}
                        />
                    )}

                    <Divider />

                    {/* BOXES PARAM */}
                    <FormColumn
                        attribute="boxes"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BOXES_LABEL'
                        )}
                        left={{
                            enabled: watchLeftEnabled,
                            defaultValue: selectedBoxesValue,
                            name: 'leftQuantity',
                            values: boxesValueOptions,
                        }}
                        right={{
                            enabled: watchRightEnabled,
                            defaultValue: selectedBoxesValue,
                            name: 'rightQuantity',
                            values: boxesValueOptions,
                        }}
                    />
                </div>
            </form>

            <div className="contact-lens-prescription-errors">
                {Object.keys(methods.formState.errors).length > 0 && (
                    <div>{getLabelFallback('CL_PDP_ERROR')}</div>
                )}
            </div>
            <div id="prescription-error-cl" className="contact-lens-prescription-errors hide">
                {getLabelFallback('CL_PDP_PRESCRIPTION_NOT_AVAIABLE')}
            </div>

            <CLDiscounts
                setQuantity={boxes => {
                    methods.setValue('leftQuantity', boxes.toString());
                    methods.setValue('rightQuantity', boxes.toString());
                }}
            />
        </FormProvider>
    );
};

export default NewPrescriptionForm;
